import { useQuery } from 'react-query';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { TableBlockConfig } from '@reports/domain/value-objects/blocks/TableBlockConfig';
import { useReportsState } from '@reports/presentation/contexts/useReportsState';
import { buildReportBlockAnalytics } from '@reports/presentation/utils';
import { getBlocksUseCases } from '@reports/composition';

export type UseTableBlockProps = {
  request: BlockDataRequestDto;
  blockConfig: TableBlockConfig;
  enabled?: boolean;
};

/**
 * Hook to fetch table block data
 *
 * Uses React Query to fetch and cache table block data based on the provided
 * block configuration. The endpoint is extracted from the blockConfig.
 * Gets blocks use cases from composition root.
 *
 * @param {Object} props - Hook properties
 * @param {BlockDataRequestDto} props.request - Request filters and date ranges
 * @param {TableBlockConfig} props.blockConfig - Table block configuration instance
 * @param {boolean} [props.enabled=true] - Whether the query should be enabled
 * @returns {Object} Table block data, error, state and refetch function
 *
 * @example
 * const { tableBlockData, tableBlockError, tableBlockState, refetchTableBlock } = useReportTableBlock({
 *   request: { startDate: new Date('2025-01-01'), endDate: new Date('2025-01-02'), ... },
 *   blockConfig: tableBlockConfig,
 *   enabled: true
 * });
 */
export const useReportTableBlock = ({ request, blockConfig, enabled = true }: UseTableBlockProps) => {
  const { fetchTableBlock } = getBlocksUseCases();
  const { state } = useReportsState();
  const { logEvent } = useAmplitude();

  const { data, error, refetch, isError, isFetching, isSuccess } = useQuery({
    queryKey: ['reports', 'block', blockConfig.key, request],
    queryFn: () => fetchTableBlock.execute(request, blockConfig),
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
    onSuccess: () => {
      const reportName = state.currentReport?.type || 'unknown';
      const analyticsData = buildReportBlockAnalytics(request, blockConfig.key, reportName);
      logEvent('@event.$reports.reportViewed', `@@section.${state.currentReport?.type}`, analyticsData);
    },
  });

  return {
    tableBlockData: data,
    tableBlockError: error,
    tableBlockState: { isFetching, isError, isSuccess },
    refetchTableBlock: refetch,
  };
};
